/**
 * Canonical unit vocabulary for priced lines.
 *
 * MIRROR of `fieldiq-api/src/constants/units.ts`. The API is the source of
 * truth; this file exists so the browser stops carrying its own answer. The
 * values, the alias map and normalizeUnit below are lifted from that file
 * verbatim, doc comments included — if you are changing one, change it there
 * first, then port it here. The single deviation is one redundant regex escape,
 * marked inline in normalizeUnit.
 *
 * Nothing in the database enforces this vocabulary. `rate_sheet_items.unit`,
 * `ticket_line_items.unit` and `quote_line_items.unit` are all varchar with no
 * CHECK constraint, so a unit that is not in this list is a normal thing to
 * read back and must survive being displayed. Do not use this module to
 * silently rewrite stored data — see RateSheetDetail, which renders a
 * non-canonical stored unit as-is rather than blanking it.
 *
 * Before this file the frontend had THREE copies of the vocabulary and TWO of
 * the normalizer:
 *
 *   NewTicketScreen.tsx / FieldTicketsScreen.tsx   8 values, duplicated
 *   QuoteCreateScreen.tsx                          a different 10
 *
 * All three were missing values the API has had since #326, so five canonical
 * units normalized to null in the browser. Since #327 that is no longer a 500;
 * it is a silently blank unit on a priced line, which is harder to spot.
 *
 * UNIT_PROMPT_ENUM is deliberately NOT ported: it exists to interpolate into
 * AI system prompts, and no prompt is built in the browser.
 */

/** The 13 canonical units, in the order the rate-sheet editor renders them. */
export const UNIT_OPTIONS = [
  { value: 'per_metre', label: 'Per Metre' },
  { value: 'per_foot', label: 'Per Foot' },
  { value: 'per_hour', label: 'Per Hour' },
  { value: 'per_day', label: 'Per Day' },
  { value: 'flat_fee', label: 'Flat Fee' },
  { value: 'per_job', label: 'Per Job' },
  { value: 'per_person_day', label: 'Per Person/Day' },
  { value: 'per_joint', label: 'Per Joint' },
  { value: 'each', label: 'Each' },
  { value: 'per_km', label: 'Per KM' },
  { value: 'per_load', label: 'Per Load' },
  { value: 'per_trip', label: 'Per Trip' },
  { value: 'custom', label: 'Custom' },
] as const;

export type UnitValue = (typeof UNIT_OPTIONS)[number]['value'];

export const UNIT_VALUES: readonly string[] = UNIT_OPTIONS.map((u) => u.value);

/** True if the value is one of the 13 canonical units. */
export function isCanonicalUnit(value: string): value is UnitValue {
  return UNIT_VALUES.includes(value);
}

/** The canonical label for a unit, or null when there is none. */
export function unitLabel(value: string): string | null {
  return UNIT_OPTIONS.find((u) => u.value === value)?.label ?? null;
}

/**
 * Spellings absorbed on read, keyed on the normalised form (lower-cased, with
 * runs of whitespace and hyphens collapsed to `_`).
 *
 * This is an explicit map and NOT a set of substring regexes, deliberately.
 * The previous implementation tested `/hour/` as a bare substring, so
 * `per 12 hours` resolved to `per_hour` — a $1050 twelve-hour Thread
 * Supervisor shift priced as one hour, or twelve of them if a quantity was
 * entered. Nothing in the UI or the PDF disclosed the rewrite. Every entry
 * below is an exact key, so no input can be captured by accident.
 *
 * Spellings deliberately ABSENT, each because it has no honest canonical
 * target and guessing one moves money (all present in TNT's price book):
 *
 *   per_man      — per PERSON with no day dimension. `per_person_day` would
 *                  add a dimension the row does not have.
 *   per_pail / per_bag / per_box / per_kit
 *                — containers. `each` is arguable but erases the container,
 *                  and the size is recorded in the name only sometimes
 *                  ("10L Pail" vs a bare "API Thread Lube" at the same price).
 *   per_liter    — a measured quantity. No canonical dimension exists.
 *   per_move     — a relocation charge, which is not a trip.
 *
 * Those return null and are surfaced to a human rather than resolved.
 */
export const UNIT_ALIASES: Record<string, string> = {
  // per_metre
  m: 'per_metre',
  '/m': 'per_metre',
  per_m: 'per_metre',
  metre: 'per_metre',
  metres: 'per_metre',
  meter: 'per_metre',
  meters: 'per_metre',
  per_meter: 'per_metre',
  per_meters: 'per_metre',
  per_metres: 'per_metre',

  // per_foot
  ft: 'per_foot',
  '/ft': 'per_foot',
  per_ft: 'per_foot',
  foot: 'per_foot',
  feet: 'per_foot',
  per_feet: 'per_foot',

  // per_hour
  hr: 'per_hour',
  hrs: 'per_hour',
  '/hr': 'per_hour',
  per_hr: 'per_hour',
  hour: 'per_hour',
  hours: 'per_hour',
  hourly: 'per_hour',
  per_hours: 'per_hour',

  // per_day
  day: 'per_day',
  days: 'per_day',
  daily: 'per_day',
  '/day': 'per_day',
  per_days: 'per_day',

  // flat_fee
  flat: 'flat_fee',
  flat_rate: 'flat_fee',
  flat_sum: 'flat_fee',
  lump: 'flat_fee',
  lump_sum: 'flat_fee',
  lumpsum: 'flat_fee',

  // per_job
  job: 'per_job',
  jobs: 'per_job',
  per_jobs: 'per_job',

  // per_joint
  jt: 'per_joint',
  per_jt: 'per_joint',
  joint: 'per_joint',
  joints: 'per_joint',
  per_joints: 'per_joint',

  // each
  ea: 'each',
  unit: 'each',
  units: 'each',
  per_each: 'each',
  per_unit: 'each',

  // per_km
  km: 'per_km',
  kms: 'per_km',
  per_kms: 'per_km',
  kilometre: 'per_km',
  kilometres: 'per_km',
  kilometer: 'per_km',
  kilometers: 'per_km',
  per_kilometre: 'per_km',
  per_kilometer: 'per_km',

  // per_load
  load: 'per_load',
  loads: 'per_load',
  per_loads: 'per_load',

  // per_trip
  trip: 'per_trip',
  trips: 'per_trip',
  per_trips: 'per_trip',

  // per_person_day — `per man per day` is the one TNT spelling with a clean
  // target here. `per man` alone is NOT, and is absent above.
  man_day: 'per_person_day',
  per_man_day: 'per_person_day',
  per_man_per_day: 'per_person_day',
  person_day: 'per_person_day',
  per_person_per_day: 'per_person_day',
};

/**
 * Resolve a free-text unit to its canonical value, or null when there is no
 * honest target.
 *
 * null means "this cannot be represented", NOT "empty". Callers must treat it
 * as something a human has to resolve — see `ticket-ai.service.ts`, which
 * degrades the line to a blank unit and says so in the draft's reasoning
 * rather than persisting a guess.
 *
 * THE DIGIT GUARD IS ABSOLUTE AND RUNS FIRST. A unit carrying a quantity is a
 * period rate — `5 day`, `per 12 hours`, `per 24 hour` — and the canonical
 * vocabulary has no way to express a period. Folding one onto the nearest
 * single-unit value is a multiplication error, not a rounding error: TNT's
 * book carries 100 reachable rows spelled this way, including 92 `5 day`
 * handling-tool rentals whose meaning (a 5-day term vs a 5+ day tier) is a 5×
 * billing difference still open with the customer.
 *
 * No canonical value contains a digit, so the guard cannot swallow one — a
 * test asserts that invariant so it cannot quietly become false.
 */
export function normalizeUnit(raw: string | null | undefined): string | null {
  if (!raw) return null;

  const lower = String(raw).toLowerCase().trim();
  if (!lower) return null;

  // Absolute, and before any matching. See the note above.
  if (/[0-9]/.test(lower)) return null;

  // The API writes this class as /[\s\-]+/g. The escape on the hyphen is
  // redundant inside a character class and ESLint's no-useless-escape flags
  // it, so this is the ONE character where the port deviates. Same class,
  // same behaviour; the hyphen cases are asserted in units.test.ts.
  const key = lower.replace(/[\s-]+/g, '_');

  if (isCanonicalUnit(key)) return key;
  return UNIT_ALIASES[key] ?? null;
}
